import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Spin, Tag, Input, DatePicker, Row, Col } from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;

const SalesList = () => {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const navigate = new useNavigate();

    const fetchSales = async (query = {}) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/sales', { params: query });
            setSales(response.data);
        } catch (error) {
            message.error('Failed to fetch sales');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSales(); // Initial fetch
    }, []);

    useEffect(() => {
        // Construct query object based on search text and date range
        const query = {};
        if (searchText) query.search = searchText;
        if (dateRange[0] && dateRange[1]) {
            query.start_date = dateRange[0].format('YYYY-MM-DD');
            query.end_date = dateRange[1].format('YYYY-MM-DD');
        }
        fetchSales(query);
    }, [searchText, dateRange]);

    const columns = [
        {
            title: 'Invoice/Receipt#',
            key: 'ref_number',
            render: (text, record) => record.invoice_number || record.receipt_number,
        },
        {
            title: 'Customer Name',
            dataIndex: ['customer', 'name'], // Nested property access
            key: 'customer_name',
            render: (name) => name || 'N/A',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Date',
            render: (text, record) => {
                const date = record.type === 'invoice' ? record.due_date : record.payment_date;
                return moment(date).format('D/M/YYYY'); // Format date to 1/1/2025
            },
            key: 'date',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag
                    color={
                        {
                            drafted: 'default',
                            processed: 'orange',
                            reversed: 'green',
                            paid: 'blue',
                            canceled: 'red'
                        }[status] || ''
                    }
                >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                </Tag>
            ),
        },
        // Add to your sales list table columns
        {
            title: 'Allocation Status',
            dataIndex: 'allocation_status',
            key: 'allocation_status',
            render: (status) => {
                const colors = {
                    released: 'blue',
                    picked: 'cyan',
                    shipped: 'green',
                    in_transit: 'orange',
                    returned: 'red',
                    closed: 'gray'
                };
                return (
                    <Tag color={colors[status] || 'default'}>
                        {status?.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button onClick={() => handleView(record.id)}>View</Button>
                    <Button
                        onClick={() => handleEdit(record.id)}
                        disabled={!['proforma'].includes(record.status)}
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        onClick={() => navigate(`/order-tracking/${record.id}`)}
                        disabled={['proforma', 'drafted'].includes(record.status)}
                    >
                        Track Order
                    </Button>
                </div>
            ),
        },
    ];

    const handleEdit = (id) => {
        window.location.href = `/sales/edit/${id}`;
    };

    const handleView = (id) => {
        window.location.href = `/sales/view/${id}`;
    };

    return (
        <Spin spinning={loading} tip="Loading...">
            <div>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={12}>
                        <Input
                            placeholder="Search by customer, invoice, or receipt number"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>
                <Table
                    dataSource={sales}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                    scroll={{ x: 'max-content' }}
                    style={{ maxWidth: '100%' }}
                />
            </div>
        </Spin>
    );
};

export default SalesList;
